import type { BlockDef, PageDef } from "./types.ts";

/** The parts of a page the builder edits. */
export interface Draft {
  name: string;
  title: string;
  blocks: BlockDef[];
}

export type PageOp =
  | { op: "page.update"; page: string; set: { title: string } }
  | { op: "page.block.remove"; page: string; block: string }
  | { op: "page.block.move"; page: string; block: string; after: string | null }
  | { op: "page.block.add"; page: string; after: string | null; block: BlockDef }
  | { op: "page.block.update"; page: string; block: string; set: Record<string, unknown> };

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * The changeset ops that turn the published page into the draft: the same
 * ops the AI sends. Order: title, removals, moves, then adds and updates in
 * draft order, so each `after` already exists when its op runs.
 */
export function pageOps(pub: Draft, draft: Draft): PageOp[] {
  const page = pub.name;
  const ops: PageOp[] = [];
  const P = new Map(pub.blocks.map((b) => [b.id, b]));
  const D = new Map(draft.blocks.map((b) => [b.id, b]));
  if (pub.title !== draft.title) ops.push({ op: "page.update", page, set: { title: draft.title } });
  for (const b of pub.blocks) if (!D.has(b.id)) ops.push({ op: "page.block.remove", page, block: b.id });

  // Reorder the blocks that stay, simulating each move.
  let cur = pub.blocks.filter((b) => D.has(b.id)).map((b) => b.id);
  const target = draft.blocks.filter((b) => P.has(b.id)).map((b) => b.id);
  target.forEach((id, i) => {
    if (cur[i] === id) return;
    cur = cur.filter((x) => x !== id);
    cur.splice(i, 0, id);
    ops.push({ op: "page.block.move", page, block: id, after: i === 0 ? null : target[i - 1]! });
  });

  draft.blocks.forEach((b, i) => {
    const after = i === 0 ? null : draft.blocks[i - 1]!.id;
    const old = P.get(b.id);
    if (!old) {
      ops.push({ op: "page.block.add", page, after, block: b });
      return;
    }
    const set: Record<string, unknown> = {};
    for (const k of new Set([...Object.keys(old), ...Object.keys(b)])) {
      const bk = (b as Record<string, unknown>)[k];
      if (!same((old as Record<string, unknown>)[k], bk)) set[k] = bk ?? null;
    }
    if (Object.keys(set).length) ops.push({ op: "page.block.update", page, block: b.id, set });
  });
  return ops;
}

export const draftOf = (p: PageDef): Draft => ({ name: p.name, title: p.title, blocks: structuredClone(p.blocks) });

/** A new block id that is not in use. */
export function newBlockId(taken: { id: string }[]): string {
  let id = "";
  do id = `b_${Math.random().toString(36).slice(2, 8)}`;
  while (id.length < 5 || taken.some((b) => b.id === id));
  return id;
}

/** Moves `id` to just after `after` (null = first). */
export function moveAfter(blocks: BlockDef[], id: string, after: string | null): BlockDef[] {
  if (id === after) return blocks;
  const list = blocks.filter((b) => b.id !== id);
  const b = blocks.find((x) => x.id === id);
  if (!b) return blocks;
  const i = after === null ? 0 : list.findIndex((x) => x.id === after) + 1;
  list.splice(i, 0, b);
  return list;
}
